/**
 * Audit log – records activity of all users (page views, sign-in, entity changes).
 * All user actions should be logged; callers must pass { userId, userEmail, displayName } in context
 * so every log is attributed. Visible and readable by super admin only.
 * Firestore rules enforce read: isSuperAdmin(); create: any authenticated user.
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

export const AUDIT_ACTIONS = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
};

export const AUDIT_ENTITIES = {
  VENUE: 'venue',
  COURT: 'court',
  VENUE_BOOKING: 'venue_booking',
  VENUE_BLOCK: 'venue_block',
  VENUE_ADMIN: 'venue_admin',
  TOURNAMENT: 'tournament',
  MATCH: 'match',
  REGISTRATION: 'registration',
  USER: 'user',
  APARTMENT: 'apartment',
  PRICING: 'pricing',
  USER_ACTIVITY: 'user_activity',
};

/**
 * Log an audit entry. Call from app code after successful create/update/delete.
 * @param {string} action - AUDIT_ACTIONS.CREATE | UPDATE | DELETE
 * @param {string} entityType - AUDIT_ENTITIES.*
 * @param {string} entityId - document id or identifier
 * @param {object} context - { userId, userEmail, displayName }
 * @param {object} [changes] - before/after or description of change
 * @param {object} [metadata] - extra (e.g. venueId, tournamentId)
 */
export async function logAudit(action, entityType, entityId, context, changes, metadata) {
  try {
    await addDoc(collection(db, 'auditLogs'), {
      action,
      entityType,
      entityId: entityId || '',
      userId: context?.userId || null,
      userEmail: context?.userEmail || null,
      displayName: context?.displayName || null,
      changes: changes || null,
      metadata: metadata || null,
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    console.error('[auditLog] Failed to write:', err);
  }
}

/**
 * Recursively remove undefined values (Firestore does not accept undefined).
 * @param {unknown} value
 * @returns {unknown}
 */
function sanitizeForFirestore(value) {
  if (value === undefined) return null;
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(sanitizeForFirestore);
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, sanitizeForFirestore(v)])
  );
}

/**
 * Log user activity for super-admin visibility (page views, button clicks, key actions).
 * Writes to auditLogs with entityType USER_ACTIVITY. All users' activity is logged; only super admins can read.
 * @param {object} context - { userId, userEmail, displayName }
 * @param {string} action - e.g. 'page_view', 'button_click', 'match_start', 'match_complete', 'reschedule', 'profile_edit'
 * @param {string} [page] - Path or page name
 * @param {string} [label] - Button or element label
 * @param {object} [metadata] - Extra { tournamentId, matchId, ... }
 */
export async function logUserActivity(context, action, page, label, metadata) {
  try {
    const meta = {
      page: page ?? null,
      label: label ?? null,
      ...(metadata && typeof metadata === 'object' ? metadata : {}),
    };
    await addDoc(collection(db, 'auditLogs'), {
      action: action || 'activity',
      entityType: AUDIT_ENTITIES.USER_ACTIVITY,
      entityId: context?.userId || '',
      userId: context?.userId ?? null,
      userEmail: context?.userEmail ?? null,
      displayName: context?.displayName ?? null,
      changes: null,
      metadata: sanitizeForFirestore(meta),
      timestamp: serverTimestamp(),
    });
  } catch (err) {
    console.error('[auditLog] User activity write failed:', err);
  }
}
